/**
 * Band der Kanalregeln über dem Tagesraster (Board „Vertrieb“) mit dem Weg „verwalten“ in den
 * Reiter. Eine Regel ist erst wirksam, wenn Channex sie zurückgemeldet hat: gefüllt heißt
 * bestätigt, alles andere ist gestrichelt – fehlgeschlagen rot, offen grau, Abgleich bernstein.
 * Eine leere Spur behauptet nichts: der Server lässt Regeln weg, die auch Apartments außerhalb der
 * eigenen Sicht schließen.
 */
import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import type { AvailabilityDistributionChannelCloseout } from '@/shared/api';
import { closeoutStatusLabel } from '../channelCloseoutPresentation';
import { clampIso, diffIsoDays } from './calendarDates';
import { shortDate } from './calendarFormat';
import { gridColumns } from './calendarGeometry';

interface CloseoutBandRowProps {
  closeouts: AvailabilityDistributionChannelCloseout[];
  dates: string[];
  apartmentNames: Map<string, string>;
  totalApartments: number;
  sidebarWidth: number;
  dayMin: number;
  onManage?: () => void;
}

const TONE: Record<AvailabilityDistributionChannelCloseout['syncStatus'], string> = {
  Synced: 'bg-navy/10 text-navy',
  Failed: 'border-2 border-dashed border-sealwax text-sealwax',
  Pending: 'border border-dashed border-line bg-card text-ink-soft',
  NeedsReconcile: 'border border-dashed border-amber bg-amber-wash text-amber-ink',
  Drifted: 'border border-dashed border-amber bg-amber-wash text-amber-ink',
};

export function CloseoutBandRow(props: CloseoutBandRowProps) {
  const { closeouts, dates, sidebarWidth, dayMin, onManage } = props;
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return null;
  return (
    <div
      className="border-hairline bg-paper/60 grid border-b"
      style={{
        gridTemplateColumns: gridColumns(sidebarWidth, dates.length, dayMin),
        gridAutoRows: '46px',
      }}
    >
      <div
        className="bg-paper text-ink-soft sticky left-0 z-20 flex items-center gap-2 px-[18px] text-xs font-bold"
        style={{ gridRow: `1 / span ${Math.max(closeouts.length, 1)}` }}
      >
        Kanalregeln
        {onManage && (
          <button
            type="button"
            onClick={onManage}
            className="rounded-pill bg-card-alt text-ink hover:bg-hairline min-h-11 px-2.5 text-[11px] font-bold"
          >
            verwalten
          </button>
        )}
      </div>
      {closeouts.map((rule, index) => {
        const start = clampIso(rule.startDate, first, last);
        const end = clampIso(rule.endDate, first, last);
        return (
          <Band
            key={rule.id}
            rule={rule}
            row={index + 1}
            gridStart={diffIsoDays(first, start) + 2}
            span={diffIsoDays(start, end) + 1}
            targets={targetLabel(rule, props.apartmentNames, props.totalApartments)}
          />
        );
      })}
    </div>
  );
}

function Band(props: {
  rule: AvailabilityDistributionChannelCloseout;
  row: number;
  gridStart: number;
  span: number;
  targets: string;
}) {
  const { rule } = props;
  const status = closeoutStatusLabel(rule.syncStatus);
  const text = `${rule.channels.map(channelLabel).join(' und ')} geschlossen · ${props.targets} · bis ${shortDate(rule.endDate)}`;
  return (
    // contain:inline-size — im min-w-max-Raster verbreiterte der Text sonst jede Tagesspalte.
    <div
      className="flex items-center px-1 [contain:inline-size]"
      style={{ gridColumn: `${props.gridStart} / span ${props.span}`, gridRow: props.row }}
    >
      <div
        className={cn(
          'flex h-[30px] w-full min-w-0 items-center gap-2 rounded-[10px] px-3 text-xs font-bold',
          TONE[rule.syncStatus],
        )}
        title={`${rule.name}: ${text} — ${status}`}
      >
        {rule.channels.slice(0, 2).map((channel) => (
          <OtaLogo key={channel} channel={channel} size={14} />
        ))}
        <span className="min-w-0 truncate">{text}</span>
        <span className="ml-auto shrink-0">{status}</span>
      </div>
    </div>
  );
}

function channelLabel(channel: string): string {
  if (channel === 'BookingCom') return 'Booking.com';
  return channel;
}

function targetLabel(
  rule: AvailabilityDistributionChannelCloseout,
  names: Map<string, string>,
  total: number,
): string {
  if (total > 0 && rule.propertyIds.length >= total) return 'alle Apartments';
  const labels = rule.propertyIds.map((id) => names.get(id) ?? '—');
  if (labels.length <= 4) return `Apartments ${labels.join(', ')}`;
  return `Apartments ${labels.slice(0, 4).join(', ')} +${labels.length - 4}`;
}
